/**
 * Formats a phone number into the pattern +998 (00) 000-00-00.
 *
 * @param {string} phone - The raw phone number string.
 * @param {object} options - Options object.
 * @param {boolean} [options.masked=false] - Whether to mask the phone number.
 * @returns {string | null} The formatted phone number, or the masked version if `masked` is true.
 *
 */

export const formatPhoneNumber = (phone, options = { masked: false }) => {
  if (typeof phone !== 'string') {
    return null;
  }

  let formatted = phone.trim();

  if (formatted.length === 12) {
    formatted = formatted.slice(3);
  }

  const [providerCode, midTwoDigits, lastTwoDigits] = [
    formatted.slice(0, 2),
    formatted.slice(5, 7),
    formatted.slice(7, 9),
  ];

  return options.masked
    ? `+998 (${providerCode}) ${formatted.slice(2, 3)}**-**-${lastTwoDigits}`
    : `+998 (${providerCode}) ${formatted.slice(2, 5)}-${midTwoDigits}-${lastTwoDigits}`;
};

/**
 * Formats a phone number as the national number without the 998 country code,
 * e.g. "998935836033" -> "93 583 60 33".
 *
 * @param {string} phone - The raw phone number string.
 * @returns {string} The formatted national number, or the original input if it can't be parsed.
 */
export const formatPhoneNational = phone => {
  if (typeof phone !== 'string') {
    return '';
  }

  const digits = phone.replace(/\D/g, '');
  const national = digits.length === 12 && digits.startsWith('998') ? digits.slice(3) : digits;

  if (national.length !== 9) {
    return phone;
  }

  const [code, first, second, third] = [
    national.slice(0, 2),
    national.slice(2, 5),
    national.slice(5, 7),
    national.slice(7, 9),
  ];

  return `${code} ${first} ${second} ${third}`;
};
